/**
 * Frontend Advanced Logic — Multi-group conditional logic evaluation.
 *
 * Intercepts GF's `gform_is_value_match` filter to handle sentinel
 * values injected by the PHP Advanced_Logic class. When a sentinel
 * `gpacl_evaluate` value is encountered, evaluates all groups
 * using the configured between-groups logic type (AND/OR).
 *
 * @package
 */

import type { GPACLData } from '../typings/shared';
import { evaluateFormula } from '../shared/helpers/formula-rule';
import { registerModule } from './module-registry';
import {
	isPassSentinel,
	isFailSentinel,
	SENTINELS,
	sentinelTargetToFieldId,
} from '../shared/sentinels';

/**
 * Reentrancy guard to prevent infinite recursion.
 *
 * When we call `gf_get_field_action()` to evaluate a group, it internally
 * triggers `gform_is_value_match` again. Without this guard, we'd recurse
 * infinitely on the `gpacl_evaluate` sentinel.
 */
let doingLogic = false;

/**
 * Evaluate all groups for a field's GPACL configuration.
 *
 * Respects the `groupLogicType` setting:
 * - 'or' (default): returns true if ANY group evaluates to "show"
 * - 'and': returns true only if ALL groups evaluate to "show"
 *
 * Short-circuits on the first decisive group for performance.
 *
 * @param formId  The form ID.
 * @param fieldId The field ID (from the sentinel rule).
 * @return True if the field should be shown.
 */
function evaluateGroups(formId: number, fieldId: string | number): boolean {
	const config = getFieldConfig(fieldId);
	if (!config) {
		// No GPACL config found — default to showing the field.
		return true;
	}

	const isAndBetweenGroups = config.groupLogicType === 'and';

	for (const group of config.groups) {
		// Build a native GF conditional logic object for this group.
		// Map 'and'/'or' back to GF's 'all'/'any' for native evaluation.
		const nativeLogicType = group.logicType === 'or' ? 'any' : 'all';

		// Pre-process formula rules: evaluate formula, do comparison,
		// convert to pre-resolved sentinel rules.
		const resolvedRules = group.rules.map((r) => {
			if (r.formula) {
				const sourceValue = evaluateFormula(r.formula, formId);
				const matches = gf_matches_operation(
					String(sourceValue),
					r.value,
					r.operator
				);
				return {
					fieldId: String(r.fieldId || '0'),
					operator: 'is',
					value: matches
						? 'gpacl_formula_pass'
						: 'gpacl_formula_fail',
				};
			}
			// User sentinel rules (gpacl_user_pass / gpacl_user_fail) have a
			// fieldId like 'gpacl_user_meta::bio' which contains '::' — a CSS
			// pseudo-element sequence that makes jQuery throw a syntax error when
			// GF tries to build a selector for the field's current value.
			// Since the sentinel value is what matters (not the field value),
			// replace the fieldId with '0' so GF skips the selector lookup.
			if (
				isPassSentinel(String(r.value)) ||
				isFailSentinel(String(r.value))
			) {
				return {
					fieldId: '0',
					operator: 'is',
					value: String(r.value),
				};
			}
			return {
				fieldId: String(r.fieldId),
				operator: r.operator,
				value: String(r.value),
			};
		});

		const groupLogic = {
			// Always use 'show' here so gf_get_field_action() returns 'show'
			// when the group's rules match. The outer sentinel conditionalLogic
			// (injected by build_sentinel_logic) carries the real actionType
			// ('show'/'hide') and GF applies it at the field level. Passing
			// config.actionType here caused a double-inversion: when actionType
			// is 'hide', a matching group returned 'hide' from gf_get_field_action,
			// which GPACL then treated as "not shown", inverting the result.
			actionType: 'show',
			logicType: nativeLogicType,
			rules: resolvedRules,
		};

		// Use GF's native evaluation to check this group.
		const action = gf_get_field_action(formId, groupLogic as any);

		if (isAndBetweenGroups) {
			// AND between groups: if any group does NOT show, field is hidden.
			if (action !== 'show') {
				return false;
			}
		} else if (action === 'show') {
			// OR between groups: if any group shows, field is visible.
			return true;
		}
	}

	// AND: all groups matched → show. OR: no group matched → hide.
	return isAndBetweenGroups;
}

/**
 * Get the GPACL configuration for a specific field from the localized config.
 *
 * @param rawFieldId The evaluate rule's fieldId (possibly prefixed).
 * @return The GPACLData for the field, or null if not found.
 */
function getFieldConfig(rawFieldId: string | number): GPACLData | null {
	const config = window.gpacl_frontend_config;
	if (!config) {
		return null;
	}

	// Strip the 'gpacl_field_' prefix PHP applies to field targets.
	const fieldId = sentinelTargetToFieldId(rawFieldId);

	// String keys: gpacl_next_*, gpacl_notif_*, gpacl_conf_*
	if (typeof fieldId === 'string' && config[fieldId]) {
		return config[fieldId];
	}

	const numericId =
		typeof fieldId === 'string' ? parseInt(fieldId, 10) : fieldId;
	return config[numericId] || null;
}

// Register with the module registry on import.
// Advanced Logic must be registered first so sentinel interception
// runs before operator modules.
registerModule({
	slug: 'advanced-logic',
	handles: (rule) =>
		isPassSentinel(rule.value) ||
		isFailSentinel(rule.value) ||
		rule.value === SENTINELS.EVALUATE,
	evaluate: (isMatch, formId, rule) => {
		// Always pass dependency-tracking and pre-resolved sentinel rules.
		if (isPassSentinel(rule.value)) {
			return true;
		}

		if (isFailSentinel(rule.value)) {
			return false;
		}

		// Reentrancy guard.
		if (doingLogic) {
			return isMatch;
		}

		doingLogic = true;

		const result = evaluateGroups(formId, rule.fieldId);

		doingLogic = false;

		return result;
	},
});
